import { Injectable } from '@angular/core';
import { PropertyConfig } from 'src/app/shared/config/property.config';
import { PropertyAttribute } from '../../property/interface/property.interface';
import { UnitAdjustmentRecommendation } from '../interface/adjustment.interface';

@Injectable({
  providedIn: 'root',
})
export class UnitAdjustmentService {
  /**
   * Resolves an adjustment value from the correct nesting level.
   * - Unit attribute + both indices → structures[sIdx].units[uIdx][fieldKey] (fallback to flat)
   * - Structure attribute + structureIndex → structures[sIdx][fieldKey] (fallback to flat)
   * - Otherwise → flat adjustmentRecord[fieldKey]
   */
  resolveAdjustmentValue(
    adjustmentRecord: UnitAdjustmentRecommendation | undefined,
    fieldKey: PropertyAttribute,
    structureIndex?: number,
    unitIndex?: number
  ): number | null {
    if (!adjustmentRecord) return null;

    const isUnitAttr = (PropertyConfig.unitAttributes as readonly string[]).includes(fieldKey);
    const isStructureAttr = (PropertyConfig.structureAttributes as readonly string[]).includes(fieldKey);

    if (isUnitAttr && structureIndex != null && unitIndex != null) {
      return (
        adjustmentRecord.structures?.[structureIndex]?.units?.[unitIndex]?.[fieldKey as PropertyAttribute] ??
        adjustmentRecord[fieldKey as PropertyAttribute] ??
        null
      );
    }

    if (isStructureAttr && structureIndex != null) {
      return (
        adjustmentRecord.structures?.[structureIndex]?.[fieldKey as PropertyAttribute] ??
        adjustmentRecord[fieldKey as PropertyAttribute] ??
        null
      );
    }

    return adjustmentRecord[fieldKey as PropertyAttribute] ?? null;
  }

  /**
   * Rebuilds the structures and units lists the backend returns into positional lists, so an entry's index
   * is its structure or unit index again. The backend keys its scoped entries by index and emits only the
   * ones carrying a value, putting the real index in `id` - read positionally, unit 2 would look like unit 0.
   */
  normalizeScopedTopology(adjustmentRecord: UnitAdjustmentRecommendation | undefined): void {
    if (!adjustmentRecord?.structures?.length) return;

    adjustmentRecord.structures = this._toPositionalList(adjustmentRecord.structures);

    adjustmentRecord.structures.forEach((structure) => {
      if (structure?.units?.length) structure.units = this._toPositionalList(structure.units);
    });
  }

  /**
   * Sets an adjustment value at the correct nesting level.
   * Initializes structures/units arrays as needed.
   */
  setAdjustmentValue(
    adjustmentRecord: UnitAdjustmentRecommendation,
    fieldKey: PropertyAttribute,
    value: number | null,
    structureIndex?: number,
    unitIndex?: number
  ): void {
    const isUnitAttr = (PropertyConfig.unitAttributes as readonly string[]).includes(fieldKey);
    const isStructureAttr = (PropertyConfig.structureAttributes as readonly string[]).includes(fieldKey);

    if (isUnitAttr && structureIndex != null && unitIndex != null) {
      if (!adjustmentRecord.structures) adjustmentRecord.structures = [];
      if (!adjustmentRecord.structures[structureIndex]) adjustmentRecord.structures[structureIndex] = {} as any;
      if (!adjustmentRecord.structures[structureIndex].units) adjustmentRecord.structures[structureIndex].units = [];
      if (!adjustmentRecord.structures[structureIndex].units![unitIndex]) {
        adjustmentRecord.structures[structureIndex].units![unitIndex] = {} as any;
      }
      adjustmentRecord.structures[structureIndex].units![unitIndex][fieldKey as PropertyAttribute] = value;
      return;
    }

    if (isStructureAttr && structureIndex != null) {
      if (!adjustmentRecord.structures) adjustmentRecord.structures = [];
      if (!adjustmentRecord.structures[structureIndex]) adjustmentRecord.structures[structureIndex] = {} as any;
      adjustmentRecord.structures[structureIndex][fieldKey as PropertyAttribute] = value;
      return;
    }

    adjustmentRecord[fieldKey as PropertyAttribute] = value;
  }

  /** Places each entry at the index its `id` names, dropping the `id` itself - it is not an adjustment field. */
  private _toPositionalList<T extends object>(entries: T[]): T[] {
    const positioned: T[] = [];

    entries.forEach((entry, index) => {
      if (!entry) return;

      const { id, ...adjustmentFields } = entry as T & { id?: number };
      positioned[id ?? index] = adjustmentFields as T;
    });

    return [...positioned];
  }
}
